import { FC, useState, useCallback, useEffect } from 'react'
import { useMutation } from '@apollo/client/react'
import { eqBy, includes, isEmpty, map, prop, symmetricDifferenceWith } from 'ramda'
import { useFlags } from 'launchdarkly-react-client-sdk'

import SearchPill from '~/src/components/generic/SearchPill'
import RadioGroup from '~/src/components/generic/RadioGroup'
import { useToast } from '~/src/components/generic/Toast/hook/useToast'
import Icon from '~/src/components/generic/PhosphorIcons/Icon'

import CostCenterAutocomplete from '~/src/components/shared/CostCenterAutocomplete/CostCenterAutocomplete'
import BaseDialog from '~/src/components/shared/BaseDialog'
import { MAX_NUMBER_COST_CENTERS } from '~/src/components/shared/features/constants'

import { useAuthenticatedUser } from '~/hooks/useAuthentication'
import { useTranslation } from '~/src/hooks/useTranslation'

import { UPDATE_USER_SETTINGS } from '~/src/components/Settings/queries'
import { MixpanelEvent, track } from '~/clients/mixpanelClient'
import { ERROR_DELAY, PAGES } from '~/src/components/Settings/constants'
import { DEFAULT_OPERATOR_HOME_PAGE } from '~/app/constants'
import { tw } from '~/lib/utils'

import {
  UpdateUserSettingsMutation,
  UpdateUserSettingsMutationVariables,
} from '~/src/components/Settings/__generated_types__/queries.gql'
import { HomePage, UserSettingsUpdate } from '~/__generated_types__/globalTypes'

import { CostCenter } from '~/src/components/Settings/types'

type Props = {
  showHomePageSetting?: boolean
  onCloseDialog: () => void
}

const Settings: FC<Props> = ({ showHomePageSetting = true, onCloseDialog }) => {
  const { t } = useTranslation('settings')
  const [costCenters, setCostCenters] = useState<Array<CostCenter>>([])
  const [showErrorMessage, setShowErrorMessage] = useState(false)
  const [user, , { loading: isCurrentUserLoading, refetch }] = useAuthenticatedUser()
  const [selectedHomePage, setSelectedHomePage] = useState<HomePage>(
    DEFAULT_OPERATOR_HOME_PAGE
  )
  const toast = useToast()
  const { favoriteCostCenterLimit, allowNewUserWorkflows } = useFlags()
  const maxCostCenters = favoriteCostCenterLimit || MAX_NUMBER_COST_CENTERS

  const [updateUserSettings, { loading }] = useMutation<
    UpdateUserSettingsMutation,
    UpdateUserSettingsMutationVariables
  >(UPDATE_USER_SETTINGS)

  useEffect(() => {
    setSelectedHomePage(user.settings.homePage ?? DEFAULT_OPERATOR_HOME_PAGE)
    setCostCenters(user.settings.favoriteCostCenters ?? [])

    if (user.settings.updatedAt === null) {
      updateUserSettings({
        variables: {
          input: {
            userId: user.id,
          },
        },
      })
    }
  }, [user, updateUserSettings])

  const handleChange = useCallback(
    (costCenter: CostCenter) => {
      if (costCenters.length > maxCostCenters) {
        setShowErrorMessage(true)

        return
      }

      if (!includes(costCenter.id, map(prop('id'), costCenters))) {
        setCostCenters([...costCenters, costCenter])
      }
    },
    [costCenters, maxCostCenters]
  )

  const removeFilter = useCallback(
    (costCenter: CostCenter) => {
      const filteredCostCenters = costCenters.filter(
        (currentCostCenter) => currentCostCenter !== costCenter
      )

      setShowErrorMessage(false)
      setCostCenters([...filteredCostCenters])
    },
    [costCenters]
  )

  const handleSave = useCallback(() => {
    if (isCurrentUserLoading) return

    const input: UserSettingsUpdate = {
      userId: user.id,
    }
    const costCentersUpdated = !isEmpty(
      symmetricDifferenceWith(
        eqBy(prop('id')),
        costCenters,
        user.settings.favoriteCostCenters
      )
    )

    if (costCentersUpdated) {
      const selectedCostCenterIds = costCenters.map((costCenter) => costCenter.id)

      input.favoriteCostCenters = selectedCostCenterIds

      track(MixpanelEvent.UserSettingsFavoriteCostCenterChanged, {
        favoriteCostCenters: selectedCostCenterIds,
      })
    }

    if (selectedHomePage !== user.settings.homePage) {
      input.homePage = selectedHomePage

      track(MixpanelEvent.UserSettingsHomePageChanged, {
        homePage: selectedHomePage,
      })
    }

    if (!input.favoriteCostCenters && !input.homePage) {
      onCloseDialog()

      return
    }

    const toastId = new Date().valueOf()
    const errorMessage = t('toast.saveError')

    updateUserSettings({
      variables: {
        input,
      },
      onCompleted: (data) => {
        if (data.updateUserSettings.__typename === 'UserSettings') {
          refetch()
          onCloseDialog()
        } else if (
          data.updateUserSettings.__typename === 'TooManyFavoriteCostCenters'
        ) {
          setShowErrorMessage(true)
        } else {
          toast.error(errorMessage, { id: toastId, delay: ERROR_DELAY })
        }
      },
      onError: () => {
        toast.error(errorMessage, { id: toastId, delay: ERROR_DELAY })
      },
    })
  }, [
    costCenters,
    selectedHomePage,
    isCurrentUserLoading,
    user,
    toast,
    onCloseDialog,
    updateUserSettings,
    refetch,
    t,
  ])

  const infoMessage = showErrorMessage
    ? t('favoriteCostCenters.hintOverLimit', { max: maxCostCenters })
    : t('favoriteCostCenters.hintWithinLimit', { max: maxCostCenters })

  return (
    <BaseDialog
      className="max-w-sm"
      isOpen={true}
      loading={loading}
      primaryButtonText={t('dialog.save')}
      title={t('dialog.title')}
      onClose={onCloseDialog}
      onConfirm={handleSave}
    >
      <div className="space-y-4">
        <div className="space-y-1">
          <div className="space-y-2">
            <div className="text-body-m font-bold">
              {t('favoriteCostCenters.sectionTitle')}
            </div>
            <CostCenterAutocomplete
              fuseThreshold={0.3}
              inputClassName="h-9 rounded w-full sm:text-body-m text-body-l"
              onSelect={handleChange}
            />
            <div className="flex max-h-[20rem] flex-wrap justify-start gap-3 overflow-auto align-middle">
              {costCenters.map((costCenter) => (
                <SearchPill
                  ariaLabel={t('removeFavoriteAria', { name: costCenter.name })}
                  className="w-fit min-w-fit rounded bg-gray-100"
                  content={costCenter.name}
                  key={costCenter.id}
                  onClose={() => removeFilter(costCenter)}
                >
                  <Icon name="HeartIcon" size="14" />
                </SearchPill>
              ))}
            </div>
          </div>
          <div
            className={tw(
              'text-body-s-emphasized text-default',
              showErrorMessage ? 'text-red-500' : ''
            )}
          >
            {infoMessage}
          </div>
        </div>
        {!allowNewUserWorkflows && showHomePageSetting && (
          <div className="space-y-2">
            <div className="text-body-m font-bold">{t('homePage.sectionTitle')}</div>
            <RadioGroup
              className="justify-start space-x-8"
              name={t('homePage.radioGroupName')}
              optionClassName="accent-gray-700"
              options={PAGES}
              value={selectedHomePage}
              onSelect={setSelectedHomePage}
            />

            <div className="text-body-s-emphasized text-default">
              {t('homePage.hint')}
            </div>
          </div>
        )}
      </div>
    </BaseDialog>
  )
}

export default Settings
